import { useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { NewDocumentModalViewContext } from "../../../Shared/NewDocumentModalView";
import { NFTabs } from "../../../Shared/NewForms/NFTabs";
import { NFTab } from "../../../Shared/NewForms/NFTab";
import { NFTabContainer } from "../../../Shared/NewForms/NFTabContainer";
import { Col, Row } from "react-bootstrap";
import { NFTextInput } from "../../../Shared/NewForms/NFTextInput";
import { NFDateInput } from "../../../Shared/NewForms/NFDateInput";
import { NFLookupCompany } from "../../../Shared/NewForms/NFLookupCompany";
import { NFLookupWarehouse } from "../../../Shared/NewForms/NFLookupWarehouse";
import { NFLookupBaseDocuments } from "../../../Shared/NewForms/NFLookupBaseDocuments";
import { FixedAssetsModernizationDto } from "../../../../dtos/documents/FixedAssetsModernizations/FixedAssetsModernizationDto";
import { FixedAssetsModernizationModel } from "../../../../models/Documents/FixedAssetsModernizationModel/FixedAssetsModernizationModel";
import { DocumentModalViewHeader } from "../../../Shared/DocumentModalViewHeader";
import { NFormTable } from "../../../Shared/NewForms/NFormTable";
import { NFLookupFixedAsset } from "../../../Shared/NewForms/NFLookupFixedAsset";
import FixedAssetsModernizationsDetailsSkeleton from "./FixedAssetsModernizationsDetailsSkeleton";
import { DocumentSaveActionType } from "../../../../helpers/DocumentHelper";
import { FixedAssetsModernizationsDetailFooter } from "./FixedAssetsModernizationsDetailFooter";
import { AppEntities } from "../../../../config/entities";
import { importFromExcel,saveObjectToExcel } from "../../../../helpers/SaveObjectToExcel";

interface IProps {
  model: FixedAssetsModernizationDto | null;
  modelUpdatedEvent: (newModel: FixedAssetsModernizationDto) => void;
}

const FixedAssetsModernizationsDetails = ({ model, modelUpdatedEvent }: IProps) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
    formState,
    getValues,
    watch,
    control,
    trigger,
    ...methods
  } = useForm<FixedAssetsModernizationModel>();

  const modalViewContext = useContext(NewDocumentModalViewContext);
  const { postHandler, saveHandler, closeHandler, cancelHandler } = modalViewContext;

  const [tabIndex, setTabIndex] = useState(0);
  const [fileName, setFileName] = useState("Modernization");

  useEffect(() => {
    console.log(model);
    if (model) {
      setValue("id", model.id);
      setValue("documentNumber", model.documentNumber);
      setValue("documentDate", model.documentDate);
      setValue("createdDate", model.createdDate);
      setValue("comment", model.comment);
      setValue("baseDocumentAtId", model.baseDocumentAtId);
      setValue("warehouseId", model.warehouseId);
      setValue("companyId", model.companyId);
      setValue("fixedAssets", model.fixedAssets);
    }
  }, [model]);

  const handleFormSubmit = async (action: DocumentSaveActionType, data: FixedAssetsModernizationModel) => {
    if (action === "save") {
      const newModel: FixedAssetsModernizationModel = { ...model, ...data };
      await saveHandler(newModel, action);
    } else if (action === "post") {
      const newModel: FixedAssetsModernizationModel = { ...model, ...data };
      await saveHandler(newModel, action);
      await postHandler(action);
    }
  };

  if (!model) {
    return (
      <>
        <FixedAssetsModernizationsDetailsSkeleton />
      </>
    );
  }

  const exportToExcel = () => {
    const data = {
      ...model
    };
    const documentInfo = {
      title: AppEntities.FixedAssetsModernization.title,
      number: model.documentNumber,
      date: model.documentDate
    };
    saveObjectToExcel(data, documentInfo);
  };

  const handleUploadFile = (file: File) => {
    importFromExcel(file, (data: any) => {
      reset(data);
    });
  };

  return (
    <FormProvider
      getValues={getValues}
      setValue={setValue}
      register={register}
      control={control}
      trigger={trigger}
      reset={reset}
      watch={watch}
      handleSubmit={handleSubmit}
      formState={formState}
      {...methods}
    >
      <NFTabs
        onTabChange={(newIndex) => {
          setTabIndex(newIndex);
        }}
      >
        <NFTab title={"Main"} />
        <NFTab title={"Products"} />
        <NFTab title={"Additional"} />
      </NFTabs>

      <div className={"modal-container-height"}>
        <DocumentModalViewHeader
          onPost={handleSubmit((data) => handleFormSubmit("post", data))}
          onSave={handleSubmit((data) => handleFormSubmit("save", data))}
          onClose={closeHandler}
          onCancel={cancelHandler}
          handleUploadFile={handleUploadFile}
          exportToExcel={exportToExcel}
          fileName={fileName}
          setFileName={setFileName}
        />
        <NFTabContainer index={0} currentIndex={tabIndex}>
          <Row>
            <Col sm={6}>
              <NFTextInput title={"Document #"} fieldName={"documentNumber"} disabled />
            </Col>
            <Col sm={3}>
              <NFDateInput title={"Document Date"} fieldName={"documentDate"} required={true} />
            </Col>
            <Col sm={3}>
              <NFDateInput title="Created Date" fieldName="createdDate" />
            </Col>
          </Row>
          <Row>
            <Col sm={6}>
              <NFLookupCompany fieldName={"companyId"} title={"Company"} />
            </Col>
            <Col sm={6}>
              <NFLookupWarehouse fieldName={"warehouseId"} title={"Warehouse"} companyId={watch("companyId")} />
            </Col>
          </Row>
          <Row>
            <Col sm={6}>
              <NFLookupBaseDocuments fieldName={"baseDocumentAtId"} title={"Base Document at ID"} />
            </Col>
          </Row>
        </NFTabContainer>
        <NFTabContainer index={1} currentIndex={tabIndex}>
          <NFormTable fieldArray={"fixedAssets"}>
            <NFTextInput fieldName="lineNo" title="lineNo" />
            <NFTextInput fieldName="newExploitationTime" title="newExploitationTime" />
            <NFLookupFixedAsset fieldName="fixedAssetId" title="Fixed Assets Modernization" />
          </NFormTable>
        </NFTabContainer>
      </div>
      <FixedAssetsModernizationsDetailFooter />
    </FormProvider>
  );
};

export default FixedAssetsModernizationsDetails;
